import { yupResolver } from '@hookform/resolvers/yup';
import { Clock, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PICKUP_WINDOW_TOOLTIP } from 'src/app/MyQuote/CreateQuote/Steps/Steps.const';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import DateSelect from 'src/components/DateSelect/DateSelect';
import DeleteModal from 'src/components/DeleteModal';
import InputMaskCmp from 'src/components/InputMaskCmp';
import MapBox from 'src/components/MapBox/MapBox';
import SelectBox from 'src/components/SelectBox/SelectBox';
import ShipperConsigneeAutocomplete from 'src/components/ShipperConsigneeAutocomplete/ShipperConsigneeAutocomplete';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { setShipperOptions, setShippers } from 'src/redux/address.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { deleteSingleAddress } from 'src/services/AddressBookServices';
import {
    createDateWithoutTimezoneConversion,
    formatTimeInput,
    getDateWithSuffixFormat,
    getFormattedDateIgnoringTime,
    isShipperConsigneeAddressValid,
    collectFormErrorPaths,
    scrollToFirstFormError,
} from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';
import * as Yup from 'yup';

import ShipperConsigneeForm from '../../../CreateQuote/Steps/ShipperConsigneeDetails/ShipperConsigneeForm';
import { ADD_QUOTE_KEYS } from '../../addQuote.const';
import { addQuoteRevalidateStep, addQuoteShipperDetails, setAddQuoteRevalidateStep } from '../../AddQuote.slice';
import { isPastCalendarDay } from '../../dateGuard.helpers';
import { DATE_TYPE, PICKUP_DATE_TYPE_OPTIONS, PICKUP_WINDOW_TYPE_OPTIONS, WINDOW_TYPE } from '../addQuoteSteps.const';
import StepperAction from '../StepperAction';
import { useSaveNextStep } from '../useSaveNextStep';
import { useRegisterStepperPersist } from '../useStepperPersist';

interface IProps {
    setHeaderChildren: React.Dispatch<React.SetStateAction<React.ReactNode>>;
}

interface IShipperDetailsForm {
    pickupSensitivity: string;
    pickupDate: Date | string | null;
    pickupEndDate: string | null;
    pickupWindowType: string;
    pickupStartTime: string | null;
    pickupEndTime: string | null;
    serviceId: number[];
    shipperId: number | null;
    shipperCompanyName: string;
    shipperAddress: {
        fullAddress: string;
        address1?: string;
        city: string;
        country: string;
        countryCode?: string;
        latitude: string;
        longitude: string;
        postal?: string;
        state: string;
        stateCode?: string;
        streetNumber?: string;
        timezone?: string;
        additionalServiceIds?: number[];
    } | null;
}

const INITIAL_DELETE_ACTION = {
    mode: '',
    shipper: false,
    isContact: false,
    contactId: null as null,
};

const ShipperDetails: FC<IProps> = ({ setHeaderChildren }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { onSaveNext, onPersist } = useSaveNextStep();
    const { appearance, serviceTypes } = useAppSelector((state: RootState) => state.basic);
    const savedData = useAppSelector(addQuoteShipperDetails);
    const revalidateStep = useAppSelector(addQuoteRevalidateStep);

    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const prevPickupWindowType = useRef<string | undefined>(undefined);

    const { shipperOptions, consigneeOptions, shippers, handleSearchShipper } = useAddressDetails();

    const [isEditId, setIsEditId] = useState(false);
    const [deleteAction, setDeleteAction] = useState(INITIAL_DELETE_ACTION);
    const [isDeleting, setIsDeleting] = useState(false);
    const [preEditId, setPreEditId] = useState<number | null>(null);
    const [action, setAction] = useState({ pickupService: false });

    const todayDate = useMemo(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC')), [appearance?.timezone]);

    const getMinimumDate = useCallback(() => createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC')), [appearance?.timezone]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                pickupSensitivity: Yup.string().required('Pickup sensitivity is required'),
                pickupDate: Yup.mixed()
                    .nullable()
                    .required(t('ValidationMessages.Pickup_Date_is_required'))
                    // Shared with the final-step submit guard (dateGuard.helpers) so a blocked
                    // "Create Quote" and this field's red border always agree on what "past" means.
                    .test('not-in-past', t('QuotingHub.QuotingDashboard.Pickup_date_alert'), (value) => !isPastCalendarDay(value as any, appearance?.timezone)),
                pickupEndDate: Yup.string()
                    .nullable()
                    .when('pickupSensitivity', {
                        is: DATE_TYPE.FLEXIBLE,
                        then: (schema) =>
                            schema
                                .required('End date is required for flexible window')
                                .test('not-in-past', t('ValidationMessages.Pickup_date_cannot_be_in_the_past'), (value) => !isPastCalendarDay(value, appearance?.timezone)),
                        otherwise: (schema) => schema.notRequired().nullable(),
                    }),
                pickupWindowType: Yup.string().required('Pickup window type is required'),
                pickupStartTime: Yup.string()
                    .nullable()
                    .test('required-when-end-filled', t('QuotingHub.CreateQuote.Pickup_Start_Time_Required'), function (value) {
                        const { pickupWindowType, pickupEndTime } = this.parent;

                        if (pickupWindowType === 'ready_time' && pickupEndTime) return !!value;

                        return true;
                    }),
                pickupEndTime: Yup.string()
                    .notRequired()
                    .nullable()
                    .test('is-after-start', 'End time must be after start time', function (endTime) {
                        const { pickupStartTime, pickupWindowType } = this.parent;

                        if (pickupWindowType !== 'ready_time') return true;

                        if (!pickupStartTime || !endTime) return true;

                        const [startHour, startMin] = pickupStartTime.split(':').map(Number);
                        const [endHour, endMin] = endTime.split(':').map(Number);
                        const startMinutes = startHour * 60 + startMin;
                        const endMinutes = endHour * 60 + endMin;

                        return endMinutes > startMinutes;
                    }),
                serviceId: Yup.array().of(Yup.number()).notRequired(),
                shipperId: Yup.number().nullable(),
                shipperCompanyName: Yup.string().when('shipperId', {
                    is: (val: any) => val !== null && val !== undefined,
                    then: (schema) => schema.required('Shipper company name is required'),
                    otherwise: (schema) => schema.nullable(),
                }),
                shipperAddress: Yup.object()
                    .shape({
                        fullAddress: Yup.string().required(t('ValidationMessages.Shipper_address_is_required')),
                        latitude: Yup.string().required(t('ValidationMessages.Shipper_latitude_is_required')),
                        longitude: Yup.string().required(t('ValidationMessages.Shipper_longitude_is_required')),
                        city: Yup.string().required('City is required'),
                        state: Yup.string().required('State is required'),
                        country: Yup.string().required('Country is required'),
                        postal: Yup.string().required('Postal code is required'),
                        streetNumber: Yup.string().required('Street number is required'),
                    })
                    .nullable()
                    .required('Shipper address is required'),
            }),
        [t, appearance?.timezone]
    );

    const defaultValues = useMemo(
        () => ({
            pickupSensitivity: savedData?.pickupSensitivity || DATE_TYPE.FIXED,
            // Clear stale past dates — show today as default; Yup validates on submit
            pickupDate: savedData?.pickupDate || todayDate,
            pickupEndDate: savedData?.pickupEndDate || null,
            pickupWindowType: savedData?.pickupWindowType || WINDOW_TYPE.OPEN,
            pickupStartTime: savedData?.pickupStartTime || null,
            pickupEndTime: savedData?.pickupEndTime || null,
            serviceId: (savedData?.serviceId as number[]) || [],
            shipperId: savedData?.shipperId || null,
            shipperCompanyName: savedData?.shipperCompanyName || '',
            shipperAddress: savedData?.shipperAddress || null,
        }),
        [savedData, todayDate, appearance?.timezone]
    );

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        trigger,
        clearErrors,
        getValues,
        formState: { errors, isSubmitted },
    } = useForm<IShipperDetailsForm>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues,
    });

    const pickupSensitivity = watch('pickupSensitivity');
    const pickupWindowType = watch('pickupWindowType');
    const pickupDate = watch('pickupDate');
    const watchShipperAddress = watch('shipperAddress');
    const watchShipperFullAddress = watch('shipperAddress')?.fullAddress;
    const watchShipperCompanyName = watch('shipperCompanyName');
    const watchShipperId = watch('shipperId');
    const watchServiceId = watch('serviceId');

    const selectedShipperBusinessHours = useMemo(() => {
        const entity = shippers?.find((s: any) => s.id === watchShipperId);
        const start = entity?.businessHoursStartTime ? String(entity.businessHoursStartTime).slice(0, 5) : null;
        const end = entity?.businessHoursEndTime ? String(entity.businessHoursEndTime).slice(0, 5) : null;

        return start && end ? `${start} – ${end}` : null;
    }, [watchShipperId, shippers]);

    useEffect(() => {
        const prev = prevPickupWindowType.current;
        prevPickupWindowType.current = pickupWindowType;

        if (prev === undefined || prev === pickupWindowType) return;

        const appointmentService = (serviceTypes?.PICKUP || []).find((s: any) => s.slug === 'pickup_appointment_required');

        if (!appointmentService) return;

        const appointmentId: number = appointmentService.id;
        const shouldSelect = pickupWindowType === 'appointment';
        const currentIds: number[] = getValues('serviceId') || [];
        const isAlreadySelected = currentIds.includes(appointmentId);

        if (shouldSelect && !isAlreadySelected) {
            setValue('serviceId', [...currentIds, appointmentId]);
        } else if (!shouldSelect && isAlreadySelected) {
            setValue(
                'serviceId',
                currentIds.filter((id) => id !== appointmentId)
            );
        }
    }, [pickupWindowType]);

    useEffect(() => {
        if (savedData?.shipperAddress) {
            const isValid = isShipperConsigneeAddressValid(savedData.shipperAddress as any);

            if (!isValid || !savedData.shipperId) {
                setIsEditId(true);
            }
        }
    }, []);

    // ─── Pickup Additional Services ──────────────────────────────────────────────

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const textReplacements = [
        { original: 'After Hours / Weekend / Holiday Pickup', replacement: t('QuotingHub.Common.After_Hours_Pickup') },
        { original: 'Pickup Appointment Required', replacement: t('QuotingHub.Common.Pickup_Appointment') },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements.find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const handleServiceChange = (e: any) => {
        let { checked, name, value } = e.target;
        value = parseInt(value);

        if (checked) {
            setValue('serviceId', [...watchServiceId, value]);
        } else {
            const filteredServices = watchServiceId.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (name === 'in-bond') {
                // clear inbond data if stored in additionalServices in future
            }
        }
    };

    const handleEditService = (service: any) => () => {
        // Editable service modal handling (in-bond, load_insurance, etc.) — extend as needed
        void service;
    };

    const handleCloseServiceModal = useCallback(() => {
        setAction({ pickupService: false });
    }, []);

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    !excludedServices.includes(val.slug) && (val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                            <CheckBox
                                id={`service_${val.id}`}
                                onChangeFunc={handleServiceChange}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                            />
                            <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                                {val.slug === 'pickup_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`service_${val.id}`} className="cursor-pointer truncate">
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                        <p className="truncate" ref={serviceRefName}>
                                            <label htmlFor={`service_${val.id}`} className="cursor-pointer">
                                                {getReplacedText(val.name)}
                                            </label>
                                        </p>
                                    </TruncateTextCmp>
                                )}
                            </div>
                            {watchServiceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId]
    );

    const onSubmit = (formData: IShipperDetailsForm) => {
        const updatedFormData = {
            ...formData,
            pickupDate: moment(formData.pickupDate).format('YYYY-MM-DD 00:00:00'),
            pickupEndDate: formData.pickupEndDate ? moment(formData.pickupEndDate).format('YYYY-MM-DD 00:00:00') : null,
        };

        onSaveNext(updatedFormData, ADD_QUOTE_KEYS.SHIPPER_DETAILS);
    };

    // Sidebar persist — same payload shape as onSubmit, but pickupDate is guarded: onSubmit only
    // runs after yup validation (pickupDate is required there), while this can fire mid-fill
    // before the user has picked a date, and moment(null).format() would store "Invalid date".
    useRegisterStepperPersist(() => {
        const formData = getValues();

        onPersist(
            {
                ...formData,
                pickupDate: formData.pickupDate ? moment(formData.pickupDate).format('YYYY-MM-DD 00:00:00') : null,
                pickupEndDate: formData.pickupEndDate ? moment(formData.pickupEndDate).format('YYYY-MM-DD 00:00:00') : null,
            },
            ADD_QUOTE_KEYS.SHIPPER_DETAILS
        );
    });

    // Scroll the first invalid field into view when Next is blocked. RHF's onInvalid callback is
    // the only place the freshly-computed errors are available synchronously — a closure over
    // formState.errors would still hold the value from before validation ran.
    const onInvalid = useCallback((formErrors: any) => {
        scrollToFirstFormError(collectFormErrorPaths(formErrors));
    }, []);

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) return false;

        handleSubmit(onSubmit)();

        return true;
    }, [trigger, handleSubmit]);

    useEffect(() => {
        setHeaderChildren(<StepperAction handleNext={handleSubmit(handleNext, onInvalid)} nextButtonText={t('Common.Next')} isNextButtonDisabled={isEditId} />);

        return () => setHeaderChildren(null);
    }, [isEditId]);

    // A create blocked on a later step by a stale pickup date lands the user back here (see
    // blockOnPastDates in useSaveNextStep). The form mounts pristine, so run yup immediately —
    // that paints the red border on the pickup date — and scroll it into view. The flag is cleared
    // first so this fires exactly once per bounce.
    useEffect(() => {
        if (revalidateStep !== ADD_QUOTE_KEYS.SHIPPER_DETAILS) return;

        dispatch(setAddQuoteRevalidateStep(null));

        trigger().then((isValid) => {
            // Targeted rather than derived from formState.errors: those are not readable
            // synchronously here (the closure predates the validating render), and the only reason
            // this path runs is a date the user has to fix.
            if (!isValid) scrollToFirstFormError(['pickupDate']);
        });
    }, [revalidateStep]);

    // Pickup sensitivity: switching flexible → fixed clears pickupEndDate
    const handlePickupSensitivityChange = (onChange: any) => (value: string) => {
        onChange(value);

        if (value === 'fixed') {
            setValue('pickupEndDate', null);

            if (isSubmitted) clearErrors('pickupEndDate');
        }
    };

    // Pickup date: clear pickupEndDate if it's before new pickupDate
    const handlePickupDateChange = (onChange: any) => (event: any) => {
        const { value } = event;
        onChange(value);

        const currentPickupEndDate = getValues('pickupEndDate');

        if (currentPickupEndDate && value && moment(currentPickupEndDate).isBefore(moment(value), 'day')) {
            setValue('pickupEndDate', null);

            if (isSubmitted) trigger('pickupEndDate');
        }
    };

    // Pickup window type: switching to 'open' clears time fields (same logic as QuoteDetailTab)
    const handlePickupWindowTypeChange = (val: string, onChange: any) => () => {
        onChange(val);

        if (val === 'open') {
            setValue('pickupStartTime', null);
            setValue('pickupEndTime', null);
        } else if (val === 'appointment') {
            setValue('pickupEndTime', null);
        }

        clearErrors(['pickupStartTime', 'pickupEndTime']);

        if (isSubmitted) trigger('pickupStartTime');
    };

    const handleTimeInputChange = (event: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void, previousValue: string) => {
        const formatted = formatTimeInput(event.target.value, previousValue);
        onChange(formatted);
    };

    // ---------- Shipper address handlers ----------

    const triggerShipperValidation = () => {
        if (isSubmitted) {
            trigger('shipperCompanyName');
            trigger('shipperAddress');
        }
    };

    const resetShipperForm = () => {
        setValue('shipperCompanyName', '');
        setValue('shipperAddress', null);
    };

    const handleShipperChange = (onChange: any) => (value: any) => {
        if (value) {
            onChange(value.value);
            const data = shippers.find((s: any) => s.id === value.value);

            if (data) {
                setValue('shipperCompanyName', data.companyName);
                setValue('shipperAddress', data);
                // `pickupAdditionalService` is set by the create/update response.
                // Fall back to `additionalServiceIds` (raw DB field) in case the
                // list endpoint or a future caller omits the renamed field.
                const pickupServices = (data.pickupAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
                setValue('serviceId', pickupServices);
                triggerShipperValidation();

                if (!isShipperConsigneeAddressValid(data)) {
                    setIsEditId(true);
                }
            }
        } else {
            onChange(null);
            resetShipperForm();
        }
    };

    const handleGoogleAddressSelect = (addressData: any) => {
        setValue('shipperId', null);
        // Google establishments carry their business name (parseGooglePlaceDetails sets
        // companyName from place.name); seed the name field with it so the Add New Shipper form
        // opens pre-filled instead of blank. Plain street addresses return '', which keeps the
        // previous clear-it behaviour. Matches WALHQ's "company name from address search".
        setValue('shipperCompanyName', addressData?.companyName ?? '');
        setValue('shipperAddress', {
            fullAddress: addressData?.fullAddress ?? '',
            address1: addressData?.address1 ?? '',
            city: addressData?.city ?? '',
            country: addressData?.country ?? '',
            countryCode: addressData?.countryCode ?? '',
            latitude: addressData?.latitude ?? '',
            longitude: addressData?.longitude ?? '',
            postal: addressData?.postal ?? '',
            state: addressData?.state ?? '',
            stateCode: addressData?.stateCode ?? '',
            streetNumber: addressData?.streetNumber ?? '',
            timezone: addressData?.timezone ?? '',
            additionalServiceIds: addressData?.additionalServiceIds ?? [],
        });
        triggerShipperValidation();
        setIsEditId(true);
    };

    const handleAutocompleteChange = (value: any) => {
        if (!value) {
            if (!watchShipperFullAddress) setValue('shipperAddress', null);

            return;
        }

        if (value.type === 'blur' || (value.target && value.type)) {
            if (!watchShipperFullAddress) setValue('shipperAddress', null);

            return;
        }

        if (typeof value === 'object' && !value.target && (value.fullAddress === '' || (!value.fullAddress && !value.address1 && !value.city))) {
            setValue('shipperAddress', null);

            return;
        }

        if (typeof value === 'object' && !value.target && value.fullAddress !== undefined) {
            setValue('shipperAddress', {
                ...(watchShipperAddress || {}),
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? watchShipperAddress?.address1 ?? '',
                city: value.city ?? watchShipperAddress?.city ?? '',
                country: value.country ?? watchShipperAddress?.country ?? '',
                countryCode: value.countryCode ?? watchShipperAddress?.countryCode ?? '',
                latitude: value.latitude ?? watchShipperAddress?.latitude ?? '',
                longitude: value.longitude ?? watchShipperAddress?.longitude ?? '',
                postal: value.postal ?? watchShipperAddress?.postal ?? '',
                state: value.state ?? watchShipperAddress?.state ?? '',
                stateCode: value.stateCode ?? watchShipperAddress?.stateCode ?? '',
                streetNumber: value.streetNumber ?? watchShipperAddress?.streetNumber ?? '',
                timezone: value.timezone ?? watchShipperAddress?.timezone ?? '',
                additionalServiceIds: value.additionalServiceIds ?? watchShipperAddress?.additionalServiceIds ?? [],
            });
        }
    };

    const handleCardActionType = (value: string) => () => {
        if (value === 'edit_id') {
            const currentId = getValues('shipperId');
            setPreEditId(currentId ? +currentId : null);
            setIsEditId(true);
        } else if (value === 'delete_id') {
            setDeleteAction({ mode: 'delete', shipper: true, isContact: false, contactId: null });
        } else if (value === 'switch_id') {
            setValue('shipperId', null);
            resetShipperForm();
        }

        triggerShipperValidation();
    };

    const handleCreateNew = () => {
        setIsEditId(true);
        setValue('shipperId', null);
        resetShipperForm();
    };

    const handleEditShipper = (isEdit?: boolean) => () => {
        setIsEditId(isEdit ?? true);

        if (!isEdit) {
            const isValidId = preEditId && preEditId === watchShipperId;

            if (isValidId) {
                setPreEditId(null);
            } else {
                setValue('shipperId', null);
                resetShipperForm();
            }
        }
    };

    const handleSaveShipper = (data: any) => {
        setIsEditId(false);
        setValue('shipperId', data.id);
        setValue('shipperCompanyName', data.companyName);
        setValue('shipperAddress', data);
        const pickupServices = (data.pickupAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
        setValue('serviceId', pickupServices);
        setPreEditId(null);
        clearErrors('shipperAddress');
        clearErrors('shipperCompanyName');
    };

    useEffect(() => {
        if (watchShipperId) {
            const currentLatitude = getValues('shipperAddress')?.latitude;

            if (watchShipperAddress && currentLatitude) return;

            const data = shippers.find((s: any) => s.id === watchShipperId);

            if (data) {
                setValue('shipperCompanyName', data.companyName);
                setValue('shipperAddress', data);
                const pickupServices = (data.pickupAdditionalService ?? data.additionalServiceIds ?? []).map(Number).filter(Boolean);
                setValue('serviceId', pickupServices);
            }
        } else {
            const currentLatitude = getValues('shipperAddress')?.latitude;

            if (!currentLatitude && !watchShipperAddress) resetShipperForm();
        }
    }, [shippers, watchShipperId]);

    const handleShipperConsigneeInputChange = (e: any, { action: inputAction }: any) => {
        if (inputAction === 'input-change' || inputAction === 'input-blur' || inputAction === 'input-clear') {
            handleSearchShipper(e);
        }
    };

    const handleModalClose = (closeStatus: boolean) => () => {
        setDeleteAction(INITIAL_DELETE_ACTION);

        if (closeStatus) {
            setIsDeleting(true);
            const idToDelete = watchShipperId ? +watchShipperId : null;

            if (idToDelete) {
                deleteSingleAddress(idToDelete.toString())
                    .then((result: any) => {
                        if (result) {
                            setValue('shipperAddress', null);
                            setValue('shipperId', null);
                            setValue('shipperCompanyName', '');

                            dispatch(setShippers(shippers.filter((s: any) => s.id !== idToDelete)));
                            dispatch(setShipperOptions(shipperOptions.filter((o: any) => o.value !== idToDelete)));
                        }
                    })
                    .catch(async (error: any) => {
                        WalToast.error(error?.response?.data?.message || t('ValidationMessages.Shipper_delete_failed'));
                    })
                    .finally(() => setIsDeleting(false));
            } else {
                setIsDeleting(false);
            }
        }
    };

    return (
        <>
            {/* Shipper Address */}
            <div>
                {!isEditId && (
                    <div data-field-anchor="shipperAddress shipperId shipperCompanyName" className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <label className="stepper-label-common">
                                {t('QuotingHub.CreateQuote.Shipper_Details.Shipper')}
                                <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                            </label>
                        </div>
                        <div className="flex-1 space-y-3">
                            <ShipperConsigneeAutocomplete
                                control={control}
                                isShipper={true}
                                isEditId={isEditId}
                                watchCompanyName={watchShipperCompanyName}
                                watchShipperAddress={watchShipperAddress}
                                watchShipperFullAddress={watchShipperFullAddress}
                                shipperOptions={shipperOptions}
                                consigneeOptions={consigneeOptions}
                                errors={errors}
                                handleShipperChange={handleShipperChange}
                                handleCreateNew={handleCreateNew}
                                handleCardActionType={handleCardActionType}
                                onInputChange={handleShipperConsigneeInputChange}
                                fieldName="shipperId"
                                isQuoteCreateForm={true}
                                showDeleteOption={!!watchShipperId}
                                handleGoogleAddressSelect={handleGoogleAddressSelect}
                                watchShipperConsigneeId={watchShipperId}
                            />
                            <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden">
                                <MapBox
                                    draggable={false}
                                    address={watchShipperAddress?.fullAddress ? watchShipperAddress : null}
                                    id="shipperMap"
                                    onReverseAddressFunc={handleAutocompleteChange}
                                    className="rounded-lg bg-white h-full border border-utilityGray200"
                                    isDefaultSatelliteView={true}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {isEditId && (
                    <ShipperConsigneeForm
                        handleEditShipper={handleEditShipper}
                        id={watchShipperId ? +watchShipperId : undefined}
                        handleSaveShipper={handleSaveShipper}
                        isShipper={true}
                        defaultCompanyName={watchShipperCompanyName || ''}
                        defaultAddress={(watchShipperAddress as any) || null}
                        showAddressIncompleteWarning
                    />
                )}

                {deleteAction.mode === 'delete' && (
                    <DeleteModal
                        handleClose={handleModalClose}
                        moduleTitle={t('Orders.remove_from_wal_hq', { companyName: watchShipperCompanyName || '' })}
                        moduleName={t('Common.Shipper')}
                        isDeleteLoading={isDeleting}
                    />
                )}
            </div>

            {/* Pickup Sensitivity */}
            <div data-field-anchor="pickupSensitivity" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('QuotingHub.CreateQuote.Pickup_Sensitivity')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="pickupSensitivity"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <div className="flex flex-wrap gap-2">
                                {PICKUP_DATE_TYPE_OPTIONS.map((option) => {
                                    const IconComponent = option.icon;
                                    const isSelected = value === option.value;

                                    return (
                                        <ButtonCmp
                                            key={option.value}
                                            type="button"
                                            className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'}
                                            onClick={() => handlePickupSensitivityChange(onChange)(option.value)}
                                        >
                                            <IconComponent className="w-4 h-4" />
                                            {option.label}
                                        </ButtonCmp>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>

            {/* Pickup Date */}
            <div data-field-anchor="pickupDate pickupEndDate" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        {t('Common.Pickup_Date')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                {pickupSensitivity === 'flexible' ? (
                    <div className="w-full flex items-center gap-3">
                        <Controller
                            name="pickupDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    inputName="pickupDate"
                                    className="form_control"
                                    errorText={!!error}
                                    placeholder={t('Common.Start_date')}
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={getMinimumDate()}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={handlePickupDateChange(onChange)}
                                    parentClassName="flex-1"
                                    isDisplayIcon={true}
                                />
                            )}
                        />
                        <Controller
                            name="pickupEndDate"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <DateSelect
                                    label=""
                                    inputName="pickupEndDate"
                                    className="form_control"
                                    errorText={!!error}
                                    placeholder={t('Common.End_date')}
                                    dateFormat="dddd, MMMM Do, YYYY"
                                    minDate={pickupDate ? createDateWithoutTimezoneConversion(moment(pickupDate)) : getMinimumDate()}
                                    selected={value}
                                    value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                    onChangeFunc={(e: any) => onChange(e.value)}
                                    parentClassName="flex-1"
                                    isDisplayIcon={true}
                                />
                            )}
                        />
                    </div>
                ) : (
                    <Controller
                        name="pickupDate"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <DateSelect
                                label=""
                                inputName="pickupDate"
                                className="form_control"
                                errorText={!!error}
                                placeholder="Select pickup date"
                                dateFormat="dddd, MMMM Do, YYYY"
                                minDate={createDateWithoutTimezoneConversion(moment.tz(appearance?.timezone || 'UTC'))}
                                selected={value}
                                value={getDateWithSuffixFormat(getFormattedDateIgnoringTime(value, 'MMMM Do, YYYY'))}
                                onChangeFunc={handlePickupDateChange(onChange)}
                                parentClassName="w-full"
                                isDisplayIcon={true}
                            />
                        )}
                    />
                )}
            </div>

            {/* Pickup Window */}
            <div data-field-anchor="pickupWindowType pickupStartTime pickupEndTime" className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Pickup Window</label>
                </div>
                <div className="flex flex-col flex-1">
                    <div className="flex gap-x-3 flex-1">
                        <Controller
                            name="pickupWindowType"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <SelectBox
                                    parentClassName="w-[160px]"
                                    options={PICKUP_WINDOW_TYPE_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value }))}
                                    value={value ? PICKUP_WINDOW_TYPE_OPTIONS.find((opt) => value === opt.value) : null}
                                    onChangeFunc={(selectedOption: any) => handlePickupWindowTypeChange(selectedOption?.value, onChange)()}
                                    isError={!!error}
                                    placeholder="Select"
                                />
                            )}
                        />

                        {pickupWindowType !== 'open' && (
                            <Controller
                                name="pickupStartTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="pickupStartTime"
                                        placeholder="HH:MM"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}

                        {pickupWindowType === 'ready_time' && (
                            <Controller
                                name="pickupEndTime"
                                control={control}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <InputMaskCmp
                                        inputName="pickupEndTime"
                                        placeholder="HH:MM (optional)"
                                        parentClassName="flex-1"
                                        value={(value as string) ?? ''}
                                        HandleChange={(e: any) => handleTimeInputChange(e, onChange, (value as string) ?? '')}
                                        mask="99:99"
                                        isError={!!error}
                                        icon={
                                            <div className="absolute top-1/2 -translate-y-1/2 right-3">
                                                <Clock className="h-4 w-4" />
                                            </div>
                                        }
                                    />
                                )}
                            />
                        )}
                    </div>
                    {PICKUP_WINDOW_TOOLTIP[pickupWindowType] && (
                        <p className="text-xs font-normal text-grayLight600 leading-[18px] italic mt-2 mb-2">
                            {pickupWindowType === WINDOW_TYPE.OPEN && selectedShipperBusinessHours
                                ? PICKUP_WINDOW_TOOLTIP[pickupWindowType].replace('business hours', `business hours (${selectedShipperBusinessHours})`)
                                : PICKUP_WINDOW_TOOLTIP[pickupWindowType]}
                        </p>
                    )}
                </div>
            </div>

            {/* Pickup Additional Services */}
            <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('QuotingHub.Common.Pickup_Additional_Services')}
                        <div className="cursor-pointer" onClick={() => setAction({ pickupService: true })}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.PICKUP || [])}</div>
                </div>
            </div>

            {/* Modals */}
            {action.pickupService && <AdditionalServiceModal handleClose={handleCloseServiceModal} services={serviceTypes?.PICKUP || []} title={`Pickup ${t('Common.Additional_Services')}`} />}
        </>
    );
};

export default ShipperDetails;
